"use client";

import {
  Send,
  User,
  Loader2,
  Search,
  MessageSquare,
  Cake,
  Gift,
  Award,
  PartyPopper,
  UtensilsCrossed,
  Heart,
  Phone,
  AlertCircle,
} from "lucide-react";
import React, { useState, useEffect, useMemo } from "react";
import { format } from "date-fns";

import { Button } from "@/app/_module/components/ui/button";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import { Textarea } from "@/app/_module/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { Badge } from "@/app/_module/components/ui/badge";
import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/app/_module/components/ui/radio-group";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useSentimentalSettings } from "@/app/_module/hooks/useSentimentals";
import { useGetLeads } from "@/app/(authenticated)/leads/_modules/hooks/queries";
import type { Lead } from "@/app/(authenticated)/leads/_modules/types/get-leads-type";
import type { OccasionType } from "@/app/_module/services/sentimentalsService";
import communicationService from "@/app/_module/services/communicationService";

// Occasion configuration
const OCCASIONS = [
  {
    id: 'birthday' as OccasionType,
    label: 'Birthday',
    icon: Cake,
    color: "bg-pink-100 text-pink-800",
  },
  {
    id: 'policy_anniversary' as OccasionType,
    label: 'Policy Anniversary',
    icon: Award,
    color: "bg-blue-100 text-blue-800",
  },
  {
    id: 'christmas' as OccasionType,
    label: 'Christmas',
    icon: Gift,
    color: "bg-red-100 text-red-800",
  },
  {
    id: 'thanksgiving' as OccasionType,
    label: 'Thanksgiving',
    icon: UtensilsCrossed,
    color: "bg-orange-100 text-orange-800",
  },
  {
    id: 'new_year' as OccasionType,
    label: 'New Year',
    icon: PartyPopper,
    color: "bg-purple-100 text-purple-800",
  },
];

// Default messages for each occasion
const DEFAULT_MESSAGES: Record<OccasionType, string> = {
  birthday: 'Happy Birthday {{first_name}}! Hope you have an amazing day. This is a great time to review your life insurance coverage - when would be a good time for a quick call? I may be able to get you a better rate. Text or call me at {{phone}}',
  policy_anniversary: "Hi {{first_name}}! It's been a year since we got your life insurance in place. Thank you for your trust! Rates change often - when is a good time to chat and see if we can get you better coverage at a lower rate? Reply or call {{phone}}",
  christmas: "Happy Holidays {{first_name}}! Wishing you and your family a wonderful holiday season. As we head into the new year, it's a great time to review your coverage. Let me know if you'd like to schedule a quick call - {{phone}}",
  thanksgiving: "Happy Thanksgiving {{first_name}}! I'm grateful to have you as a client. Hope you enjoy time with family today. If you ever need to review your life insurance or have questions, I'm here for you - {{phone}}",
  new_year: "Happy New Year {{first_name}}! New year is a perfect time to make sure your family is protected. Would love to do a quick review of your coverage - when works best for a call? Text me back or call {{phone}}",
  lapsed_policy: "Hi {{first_name}}, I noticed your life insurance policy may have lapsed. Gaps in coverage can leave your family unprotected. I'd love to help you get covered again quickly - rates may have even improved. Text me back or call {{phone}}",
};

// Available variables
const AVAILABLE_VARIABLES = [
  { value: '{{first_name}}', label: 'First Name' },
  { value: '{{last_name}}', label: 'Last Name' },
  { value: '{{name}}', label: 'Full Name' },
  { value: '{{phone}}', label: 'Your Phone' },
];

/**
 * Auto-detect the occasion from the selected lead ("Selecting is outdated" —
 * boss). Priority: birthday near today -> policy anniversary near today
 * (one year from POLICY SOLD via uwr_date) -> a holiday within 7 days ->
 * default birthday. The radio group stays for manual override.
 */
const OCCASION_DETECT_WINDOW_DAYS = 3;

const daysUntilYearly = (source: Date, today: Date): number => {
  const thisYear = new Date(today.getFullYear(), source.getMonth(), source.getDate());
  const nextYear = new Date(today.getFullYear() + 1, source.getMonth(), source.getDate());
  const msDay = 86_400_000;
  const dThis = Math.round((thisYear.getTime() - today.getTime()) / msDay);
  if (dThis >= -OCCASION_DETECT_WINDOW_DAYS) return dThis;
  return Math.round((nextYear.getTime() - today.getTime()) / msDay);
};

const thanksgivingOf = (year: number): Date => {
  let day = 1;
  while (new Date(year, 10, day).getDay() !== 4) day++;
  return new Date(year, 10, day + 21);
};

const autoDetectOccasion = (lead: Lead): { occasion: OccasionType; reason: string } | null => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (lead.dob) {
    const dob = new Date(lead.dob);
    if (!isNaN(dob.getTime())) {
      const d = daysUntilYearly(dob, today);
      if (Math.abs(d) <= OCCASION_DETECT_WINDOW_DAYS) {
        return { occasion: 'birthday', reason: d === 0 ? 'Birthday is today' : d > 0 ? `Birthday in ${d} day${d === 1 ? '' : 's'}` : `Birthday was ${-d} day${d === -1 ? '' : 's'} ago` };
      }
    }
  }

  if (lead.uwrDate) {
    const sold = new Date(lead.uwrDate);
    if (!isNaN(sold.getTime()) && today.getTime() - sold.getTime() > 334 * 86_400_000) {
      const d = daysUntilYearly(sold, today);
      if (Math.abs(d) <= OCCASION_DETECT_WINDOW_DAYS) {
        return { occasion: 'policy_anniversary', reason: d === 0 ? 'Policy anniversary is today' : `Policy anniversary ${d > 0 ? 'in' : ''} ${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'}${d < 0 ? ' ago' : ''}` };
      }
    }
  }

  const year = today.getFullYear();
  const holidays: Array<{ occasion: OccasionType; date: Date; label: string }> = [
    { occasion: 'thanksgiving', date: thanksgivingOf(year), label: 'Thanksgiving' },
    { occasion: 'christmas', date: new Date(year, 11, 25), label: 'Christmas' },
    { occasion: 'new_year', date: new Date(year + 1, 0, 1), label: 'New Year' },
    { occasion: 'new_year', date: new Date(year, 0, 1), label: 'New Year' },
  ];
  for (const h of holidays) {
    const d = Math.round((h.date.getTime() - today.getTime()) / 86_400_000);
    if (d >= 0 && d <= 7) {
      return { occasion: h.occasion, reason: d === 0 ? `${h.label} is today` : `${h.label} in ${d} day${d === 1 ? '' : 's'}` };
    }
  }

  return null;
};

interface SendManualSentimentalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SendManualSentimentalDialog = function({
  open,
  onOpenChange,
}: SendManualSentimentalDialogProps) {
  const { toast } = useToast();
  const { data: settings } = useSentimentalSettings();

  // Lead search
  const [leadSearch, setLeadSearch] = useState("");
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);

  // Form state
  const [occasionType, setOccasionType] = useState<OccasionType>("birthday");
  const [message, setMessage] = useState(DEFAULT_MESSAGES.birthday);
  const [isSending, setIsSending] = useState(false);

  // Fetch leads for search
  const { data: leadsData, isLoading: isLoadingLeads } = useGetLeads({
    page: 1,
    limit: 10,
    query: leadSearch || undefined,
  });

  // Update message when occasion changes
  useEffect(() => {
    // Get the template message from settings based on occasion type
    let templateMessage: string | undefined;
    if (settings) {
      switch (occasionType) {
        case 'birthday':
          templateMessage = settings.birthdayMessage;
          break;
        case 'policy_anniversary':
          templateMessage = settings.policyAnniversaryMessage;
          break;
        case 'christmas':
          templateMessage = settings.christmasMessage;
          break;
        case 'thanksgiving':
          templateMessage = settings.thanksgivingMessage;
          break;
        case 'new_year':
          templateMessage = settings.newYearMessage;
          break;
      }
    }
    setMessage(templateMessage || DEFAULT_MESSAGES[occasionType]);
  }, [occasionType, settings]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setLeadSearch("");
      setSelectedLead(null);
      setOccasionType("birthday");
      setMessage(DEFAULT_MESSAGES.birthday);
      setAutoReason(null);
    }
  }, [open]);

  // Filter leads for dropdown
  const filteredLeads = useMemo((): Lead[] => {
    if (!leadsData?.items) return [];
    return leadsData.items.filter((lead: Lead) => lead.phone);
  }, [leadsData?.items]);

  // Calculate SMS segments
  const characterCount = message.length;
  const segmentCount = Math.ceil(characterCount / 160) || 1;

  // Insert variable at cursor position
  const insertVariable = (variable: string) => {
    setMessage(prev => prev + variable);
  };

  // Handle lead selection — auto-populate the occasion from the lead
  const [autoReason, setAutoReason] = useState<string | null>(null);
  const handleSelectLead = (lead: Lead) => {
    setSelectedLead(lead);
    setLeadSearch(lead.name);
    setShowLeadDropdown(false);
    const detected = autoDetectOccasion(lead);
    if (detected) {
      setOccasionType(detected.occasion);
      setAutoReason(detected.reason);
    } else {
      setAutoReason(null);
    }
  };

  // Handle send
  const handleSend = async () => {
    if (!selectedLead) {
      toast({
        title: "Select a Lead",
        description: "Please select a lead to send the message to.",
        variant: "destructive",
      });
      return;
    }

    if (!message.trim()) {
      toast({
        title: "Message Required",
        description: "Please enter a message to send.",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);
    try {
      // Send SMS using communication service
      await communicationService.sendSms({
        to: selectedLead.phone,
        text: message,
      });

      toast({
        title: "Message Sent",
        description: `Sentimental message sent to ${selectedLead.name}`,
      });

      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Failed to Send",
        description: error?.message || "Could not send the message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const selectedOccasion = OCCASIONS.find(o => o.id === occasionType);
  const OccasionIcon = selectedOccasion?.icon || Heart;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-pink-500" />
            Send Manual Sentimental
          </DialogTitle>
          <DialogDescription>
            Send a personalized message to a lead for a special occasion.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Lead Selection */}
          <div className="space-y-2">
            <Label>Select Lead</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search leads by name..."
                value={leadSearch}
                onChange={(e) => {
                  setLeadSearch(e.target.value);
                  setShowLeadDropdown(true);
                  if (!e.target.value) setSelectedLead(null);
                }}
                onFocus={() => setShowLeadDropdown(true)}
                className="pl-10"
              />
              {showLeadDropdown && leadSearch && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {isLoadingLeads ? (
                    <div className="p-3 text-center text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                    </div>
                  ) : filteredLeads.length === 0 ? (
                    <div className="p-3 text-center text-muted-foreground text-sm">
                      No leads found with phone numbers
                    </div>
                  ) : (
                    filteredLeads.map((lead) => (
                      <button
                        key={lead.id}
                        type="button"
                        onClick={() => handleSelectLead(lead)}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-3"
                      >
                        <div className="p-1.5 bg-gray-100 rounded-full">
                          <User className="h-3.5 w-3.5 text-gray-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{lead.name}</div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {lead.phone}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            {selectedLead && (
              <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                <User className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-green-800">{selectedLead.name}</span>
                <span className="text-xs text-green-600">({selectedLead.phone})</span>
              </div>
            )}
          </div>

          {/* Occasion Type — auto-populated from the selected lead */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label>Occasion Type</Label>
              {autoReason && (
                <span className="text-[11px] font-medium text-pink-600 bg-pink-50 border border-pink-200 rounded-full px-2 py-0.5">
                  Auto-selected — {autoReason}
                </span>
              )}
            </div>
            <RadioGroup
              value={occasionType}
              onValueChange={(value) => { setOccasionType(value as OccasionType); setAutoReason(null); }}
              className="grid grid-cols-2 gap-2"
            >
              {OCCASIONS.map((occasion) => {
                const Icon = occasion.icon;
                return (
                  <div key={occasion.id}>
                    <RadioGroupItem
                      value={occasion.id}
                      id={occasion.id}
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor={occasion.id}
                      className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-all peer-data-[state=checked]:border-pink-500 peer-data-[state=checked]:bg-pink-50 hover:bg-gray-50 ${
                        occasionType === occasion.id ? "border-pink-500 bg-pink-50" : ""
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="text-sm">{occasion.label}</span>
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Message</Label>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  {characterCount} chars
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {segmentCount} segment{segmentCount > 1 ? "s" : ""}
                </Badge>
              </div>
            </div>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your message..."
              rows={5}
              className="resize-none"
            />
            {/* Variable insertion */}
            <div className="flex flex-wrap gap-1">
              {AVAILABLE_VARIABLES.map((variable) => (
                <Button
                  key={variable.value}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => insertVariable(variable.value)}
                  className="h-6 px-2 text-xs"
                >
                  {variable.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Info Alert */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              Variables like {"{{first_name}}"} will be replaced with the lead's actual information when the message is sent.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={isSending || !selectedLead}
            className="bg-pink-600 hover:bg-pink-700"
          >
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Message
              </>
            )}
          </Button>
        </DialogFooter>
      </ResponsiveDialogContent>
    </Dialog>
  );
};
